/* eslint-disable sonarjs/cognitive-complexity */
import {
  takeLatest, put, select, call,
} from 'redux-saga/effects';
import { getFilterData } from '../selectors/common';
import {
  API_URLS, PAGES, MODALS, SUCCESS_MODALS, FETCH_OPTIONS,
} from '../constants';
import { requestGet, requestPost } from '../utils/request';
import { parseRoute } from '../utils/common';
import { aChangeUserPwd, aGetInit, aGetInitTennis, aGetProfileInfo, aSetPagination } from '../reducers';
import { aGetInitSuccess, aGetInitTenisSuccess, aGetInitTennisError, aGetProfileInfoSuccess, aSetFilters, aSetIsFetching, aSetIsLoading, aSetIsTennisLoading, aSetOpenModal, aSetPaginationData } from '../reducers/common';
import { aSetRoundSpecialOddsHistory } from '../reducers/roundSpecials';

/**
 * @namespace sagas/common
 */
/**
 * Set new filter
 *
 * @memberof sagas/common
 * @async
 * @param {object} action
 * @property {object} action
 * @property {object} action.payload
 * @property {number} action.payload.type
 * @returns {void}
 */
// eslint-disable-next-line
function* filter(action) {
  action.payload.callback(action.payload.filter);
}

/**
 * Set new page for pagination
 *
 * @memberof sagas/common
 * @async
 * @param {object} action
 * @property {object} action
 * @property {object} action.payload
 * @property {number} action.payload.type
 * @returns {void}
 */
function* paging(action) {
  yield put(aSetPaginationData(action.payload));
  let filters = yield select(getFilterData);
  filters = filters || {};
  filters.page = action.payload.page;
  action.payload.callback(filters);
}

/**
 * @memberof sagas/common
 * @typedef User
 * @type {object}
 * @property {Array} permissions
 * @property {boolean} requireNewPassword
 * @property {Array} roles
 * @property {number} userId
 * @property {string} username
 */
/**
 * @memberof sagas/common
 * @typedef init/response.data
 * @type {object}
 * @property {User|null} user
 * @property {string} svgVersion
 * @property {string} currency
 * @property {string} cnt
 * @property {object} selects Data for all dropdowns in app
 */
/**
 * Check user session
 *
 * @param {object} action
 * @memberof sagas/common
 * @async
 */
function* init(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.INIT,
      value: true,
    }));
    yield put(aSetIsLoading({
      isLoading: true,
    }));
    response = yield call(requestGet, API_URLS.INIT, false);
    if (response.status === -1) {
      const e = new Error(response.error.message);
      e.modal = MODALS.ERROR;
      throw e;
    } else {
      const {
        selectedMenu,
        selectedSubMenu,
        selectedMenuItem,
        heading,
      } = parseRoute(action.payload.location.pathname);
      let params = action.payload.location.search.split('?');
      params = params && params[1] && params[1].split('&');
      let tmp;
      let filters = null;
      if (params) {
        filters = {};
        params.map((x) => {
          tmp = x.split('=');
          if (tmp[0]) {
            const tmpVal1 = tmp[1].replace(/\+/g, ' ').replace(/%3A/g, ':');
            if (tmp[1].includes('%2C')) {
              filters[tmp[0]] = tmp[1].split('%2C');
            } else if (filters[tmp[0]]) {
              if (Array.isArray(filters[tmp[0]])) {
                filters[tmp[0]].push(tmpVal1);
              } else {
                const values = [];
                values.push(filters[tmp[0]]);
                values.push(tmpVal1);
                filters[tmp[0]] = values;
              }
            } else {
              filters[tmp[0]] = tmpVal1;
            }
          }
        });
      }
      if (!response.data.user) {
        action.payload.navigate(PAGES.LOGIN);
      }
      yield put(aGetInitSuccess({
        ...response.data,
        menu: selectedMenu,
        selectedSubMenu,
        selectedMenuItem,
        heading,
        filters,
      }));

      yield put(aSetRoundSpecialOddsHistory({
        roundSpecialOddsHistoryTemplate: response.data.rsOddsHistoryTemplate || [],
      }));
      
      yield put(aSetIsFetching({
        key: FETCH_OPTIONS.INIT,
        value: false,
      }));
      yield put(aSetIsLoading({
        isLoading: false,
      }));
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.INIT,
      value: false,
    }));
  }
}

/**
 *
 */
function* initTennis() {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.INIT_TENNIS,
      value: true,
    }));
    yield put(aSetIsTennisLoading({
      isLoading: true,
    }));
    const response = yield call(requestGet, API_URLS.INIT_TENNIS, false);
    if (response.status === -1) {
      const e = new Error(response.error.message);
      e.modal = MODALS.ERROR;
      throw e;
    } else {
      yield put(aGetInitTenisSuccess({
        ...response.data,
        isTennisEnabled: true,
      }));
    }
  } catch (error) {
    yield put(aGetInitTennisError({
      isTennisEnabled: false,
    }));
  } finally {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.INIT_TENNIS,
      value: false,
    }));
    yield put(aSetIsTennisLoading({
      isLoading: false,
    }));
  }
}
/**
 * @memberof sagas/common
 * @typedef getProfileInfo/response.data
 * @type {object}
 * @property {string} email
 * @property {string} lastLogin
 * @property {string} name
 * @property {Array} roles
 * @property {string} username
 */
/**
 * Get User Profile info
 *
 * @memberof sagas/common
 * @async
 */
function* getProfileInfo() {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_PROFILE_INFO,
      value: true,
    }));
    response = yield call(requestGet, API_URLS.PROFILE_INFO, true);
    if (response.status === -1) {
      const e = new Error(response.error.message);
      e.modal = MODALS.ERROR;
      throw e;
    } else {
      yield put(aGetProfileInfoSuccess({
        profileInfo: { ...response.data },
      }));
    }
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_PROFILE_INFO,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.GET_PROFILE_INFO,
      value: false,
    }));
  }
}

/**
 * Change User Password
 *
 * @memberof sagas/common
 * @async
 * @param {object} action
 * @property {object} action
 * @property {object} action.payload
 * @property {string} action.oldPassword
 * @property {string} action.newPassword
 */
function* changePassword(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.CHANGE_PASS,
      value: true,
    }));
    response = yield call(requestPost, API_URLS.CHANGE_USER_PWD, action.payload, true);
    if (response.status === -1) {
      const e = new Error(response.error.message);
      e.modal = MODALS.ERROR;
      throw e;
    } else {
      yield put(aSetOpenModal({
        modal: MODALS.SUCCESS,
        modalData: {
          message: 'Password changed',
          additionalMessage: 'successfully',
          modal: SUCCESS_MODALS.CHANGE_USER_PWD,
        },
        key: FETCH_OPTIONS.CHANGE_PASS,
        value: false,
      }));
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.CHANGE_PASS,
      value: false,
    }));
  }
}

/**
 * @memberof sagas/common
 * @async
 * @returns {void}
 */
export default function* commonSaga() {
  yield takeLatest(aSetFilters, filter);
  yield takeLatest(aSetPagination, paging);
  yield takeLatest(aGetInit, init);
  yield takeLatest(aGetInitTennis, initTennis);
  yield takeLatest(aGetProfileInfo, getProfileInfo);
  yield takeLatest(aChangeUserPwd, changePassword);
}
